import { WhatsAppTemplate } from "../whatsapp/WhatsappTemplatesTypes";


export interface InterviewSchedulingModalData {
    // Cancel Interview Modal
    showCancelInterviewModal: boolean;
    cancelInterviewData?: {
        interviewIds?: string[];
        applicantCount?: number;
        jobTitle?: string;
        companyName?: string;
        cancellationReason?: string;
    };
    // Reject Interview Modal
    showRejectInterviewModal: boolean;
    rejectInterviewData?: {
        interviewIds?: string[];
        applicantName?: string;
        applicantFullName?: string;
        jobTitle?: string;
        companyName?: string;
        rejectionReason?: string;
    };
    // Delete Slot Modal
    showDeleteSlotModal: boolean;
    deleteSlotData?: {
        slotDate?: string;
        slotTime?: string;
        applicantCount?: number;
        jobTitle?: string;
        companyName?: string;
    };
}

export interface InterviewSchedulingInitialState {
    isLoadingInterviewsSchedule: boolean;
    isSavingAppointment: boolean;
    interviewsSchedule: InterviewSchedulingInterface[];
    activeInterviewScheduleTab: InterviewScheduleTabEnum;
    interviewsScheduleFilters: InterviewSchedulingFilters;
    interviewsScheduleCount: number;
    upcomingInterviewsScheduleCount: number; // Always stores the count for upcoming interviews
    selectedInterviewIds: string[];
    selectedInterview: InterviewSchedulingInterface | null;
    isLoadingAppointments: boolean;
    appointments: InterviewAppointment[];
    isLoadingLocations: boolean;
    locations: InterviewLocation[];
    // Side menu states
    isSidebarVisible: boolean;
    sideMenuAction: InterviewSchedulingSideMenuActionEnum | null;
    selectedAppointment: InterviewAppointment | null;
    // Modal states
    showCancelInterviewModal: boolean;
    cancelInterviewData?: {
        interviewIds?: string[];
        applicantCount?: number;
        jobTitle?: string;
        companyName?: string;
        cancellationReason?: string;
    };
    showRejectInterviewModal: boolean;
    rejectInterviewData?: {
        interviewIds?: string[];
        applicantName?: string;
        applicantFullName?: string;
        jobTitle?: string;
        companyName?: string;
    };
    showDeleteSlotModal: boolean;
    deleteSlotData?: {
        slotId?: string;
        slotDate?: string;
        slotTime?: string;
        applicantCount?: number;
        jobTitle?: string;
        companyName?: string;
        locationLink?: string;
        instructions?: string;
        remainingSlots?: Array<{ id: string; date: string; startTime: string; endTime: string }>;
        onConfirm?: (slotId: string) => void;
    };
    showConfirmLocationChangeModal: boolean;
    locationChangeConfirmed: boolean; // True when user confirms location change, false when canceled
    showCancelAppointmentModal: boolean;
    cancelAppointmentData?: {
        appointmentId?: string;
        cancellationReason?: string;
        onConfirm?: () => void;
    };
    showConfirmScheduleInterviewModal: boolean;
    confirmScheduleInterviewData?: {
        applicantCount?: number;
        onConfirm?: () => void | Promise<void>;
    };
    showConfirmCancelOnboardingModal: boolean;
    confirmCancelOnboardingData?: {
        onConfirm?: () => void;
    };
    selectedApplicantsForInterviewIds: string[];
    isSendingInvitation: boolean;
    isModifyingLocation: boolean;
    isModifyingSlots: boolean;
    isCancelingAppointment: boolean;
    isRejectingInterview: boolean;
    isCancelingInterview: boolean;
    isAcceptingInterview: boolean;
    isMarkingAsNotAttended: boolean;
    isLoadingFilters: boolean;
    filterOptions: GetInterviewFiltersResponse | null;
    jobIdForApplicantInterview: string | null;
    messageTemplatesCache: WhatsAppTemplate[];
}

export interface InterviewSchedulingInterface {
    // id: string; // Commented out - use externalId instead
    externalId: string;
    applicant: {
        externalId: string;
        photoUrl: string | null;
        fullName: string | null;
        mobile: string | null;
        address: {
            city: string | null;
            district: string | null;
        }
        isOffline: boolean;
    } | null;
    appliedFor: {
        jobTitle: string | null;
        location: string | null;
        jobId: string | null;
        contractType: string | null;
    } | null;
    recruiter: {
        name: string | null;
    } | null;
    result: string | null;
    lastNote?: {
        message: string;
        createdBy?: string | null;
    } | null;
    numOfNotes?: number;
    slot: {
        date: string;
        startTime: number;
        endTime: number;
    } | null;
    status: InterviewInvitationStatusEnum | null;
    sentAt: number | null;
    respondedAt: number | null;
    updatedAt: number | null;
    declineReason?: InterviewInvitationDeclineReasonEnum | null;
    appointmentId?: string | null;
    location: {
        name?: string | null;
        url?: string | null;
        type?: InterviewLocationTypeEnum | null;
    }
}

export enum InterviewScheduleTabEnum {
    UPCOMING = 'upcoming',
    PAST = 'past',
    ALL = 'all',
}


export interface InterviewAppointmentSlot {
    id: string;
    date: string; // ISO date string
    startTime: string; // HH:mm format
    endTime: string; // HH:mm format
    confirmed: number;
    total: number;
}

export interface InterviewAppointment {
    externalId: string;
    isActive: boolean;
    createdAt: number | null;
    createdBy: {
        id: string;
        name: string;
    } | null;
    locationName: string | null;
    locationType: InterviewLocationTypeEnum;
    locationInstructions: string | null;
    locationUrl: string | null;
    slots: InterviewAppointmentSlot2[];
}

export interface InterviewAppointmentSlot2 {
    externalId: string;
    startTime: number;
    endTime: number;
    capacity: number;
    confirmedCount: number;
    isActive: boolean;
    isFull: boolean;
    updatedAt: number | null;
}

export interface InterviewLocation {
    locationInstructions: string;
    locationName: string;
    locationType: InterviewLocationTypeEnum;
    locationUrl: string;
}

export enum InterviewLocationTypeEnum {
    ONSITE = 'ON_SITE',
    ONLINE = 'ONLINE',
}

export enum InterviewSchedulingSideMenuActionEnum {
    NEW_SCHEDULE = 'NEW_SCHEDULE',
    NEW_SCHEDULE_FROM_APPOINTMENT = 'NEW_SCHEDULE_FROM_APPOINTMENT',
    EDIT_SCHEDULE = 'EDIT_SCHEDULE',
    NEW_APPOINTMENT = 'NEW_APPOINTMENT',
    EDIT_APPOINTMENT = 'EDIT_APPOINTMENT',
    EDIT_LOCATION = 'EDIT_LOCATION',
    EDIT_SLOTS = 'EDIT_SLOTS',
    CANCEL_APPOINTMENT = 'CANCEL_APPOINTMENT',
    CANCEL_INTERVIEW = 'CANCEL_INTERVIEW',
    REJECT_INTERVIEW = 'REJECT_INTERVIEW',
    APPOINTMENTS = 'APPOINTMENTS',
}

export interface CreateInterviewSlotDto {
    startTime: number; // Unix timestamp in milliseconds
    endTime: number; // Unix timestamp in milliseconds
    capacity?: number; // Maximum number of confirmed applicants (1-999)
}

export interface SaveInterviewAppointmentBody {
    locationUrl: string; // Interview location URL (Google Maps, Google Meet, Zoom, Microsoft Teams)
    locationName?: string; // Location name (required for on-site interviews), max 200 chars
    locationInstructions?: string; // Additional location instructions, max 200 chars
    slots: CreateInterviewSlotDto[]; // Interview slots (minimum 1, maximum 4)
}

export interface ModifyInterviewAppointmentLocationBody {
    appointmentId: string;
    locationUrl: string; // Interview location URL (Google Maps, Google Meet, Zoom, Microsoft Teams)
    locationName?: string; // Location name (required for on-site interviews), max 200 chars
    locationInstructions?: string; // Additional location instructions, max 200 chars
}

export interface UpdateInterviewSlotDto {
    externalId: string;
    capacity: number;
}

export interface ModifyInterviewAppointmentSlotsBody {
    appointmentId: string;
    slotsToRemove?: string[];
    slotsToAdd?: CreateInterviewSlotDto[]; // Interview slots (minimum 1, maximum 4)
    slotsToUpdate?: UpdateInterviewSlotDto[];
}

export interface SendInterviewInvitationBody {
    appointmentId: string;
    applicationExternalIds?: string[];
    offlineApplicationExternalIds?: string[];
    allowCapacityIncrease?: boolean;
    additionalCapacity?: number;
}

export interface GetInterviewsScheduleResponse {
    invitations: InterviewSchedulingInterface[];
    total: number;
}

export interface RecruiterOptionDto {
    id: string;
    name: string;
}

export interface LocationOptionDto {
    id: string;
    name: string;
    cityName?: string;
    cityEnum?: string;
    // districts?: string[];
}

export interface GetInterviewFiltersResponse {
    recruiters: RecruiterOptionDto[];
    locations: LocationOptionDto[];
    statuses: InterviewInvitationStatusEnum[];
    results: InterviewInvitationResultEnum[];
    shouldAutoSelectRecruiter: boolean;
}

export interface GetInterviewAppointmentsResponse {
    appointments: InterviewAppointment[];
    total: number;
}

export interface GetInterviewAppointmentDetailsResponse {
    appointment: InterviewAppointment;
    total: number;
}

export interface GetInterviewAppointmentSlotsResponse {
    slots: InterviewAppointmentSlot[];
    total: number;
}

export interface GetInterviewLocationsResponse {
    locations: InterviewLocation[];
    total: number;
}

export enum InterviewInvitationResultEnum {
    PASSED = "PASSED",
    PASSED_AND_ONBOARDING = "PASSED_AND_ONBOARDING",
    REJECTED = "REJECTED",
    NOT_ATTENDED = "NOT_ATTENDED"
}

export enum InterviewInvitationStatusEnum {
    PENDING = 'PENDING',
    CONFIRMED = 'CONFIRMED',
    DECLINED = 'DECLINED',
    EXPIRED = 'EXPIRED',
    CANCELLED = 'CANCELLED',
}

export enum InterviewInvitationDeclineReasonEnum {
    NOT_AVAILABLE = "NOT_AVAILABLE",
    ACCEPTED_ANOTHER_OFFER = "ACCEPTED_ANOTHER_OFFER",
    JOB_NOT_SUITABLE = "JOB_NOT_SUITABLE",
    OTHER = "OTHER"
}

export enum InterviewOperationEnum {
    REJECT = 'REJECT',
    CANCEL = 'CANCEL',
    ACCEPT = 'ACCEPT',
    MARK_AS_NOT_ATTENDED = 'MARK_AS_NOT_ATTENDED'
}

export enum InterviewAppointmentCancellationReasonEnum {
    IM_BUSY_OR_HAVE_EMERGENCY = 'IM_BUSY_OR_HAVE_EMERGENCY',
    RESCHEDULED_TO_ANOTHER_APPOINTMENT = 'RESCHEDULED_TO_ANOTHER_APPOINTMENT',
    COMPANY_REQUESTED_CANCELLATION = 'COMPANY_REQUESTED_CANCELLATION',
    OTHER = 'OTHER'
}

export enum InterviewCancellationReasonEnum {
    IM_BUSY = 'IM_BUSY',
    APPLICANT_REQUESTED_CANCELLATION = 'APPLICANT_REQUESTED_CANCELLATION',
    APPLICANT_NOT_FIT = 'APPLICANT_NOT_FIT',
    INVITE_SENT_BY_MISTAKE = 'INVITE_SENT_BY_MISTAKE',
    OTHER = 'OTHER'
}

export enum InterviewMessageTemplateTypeEnum {
    INTERVIEW_INVITATION_FLOW = 'INTERVIEW_INVITATION_FLOW',
    SCHEDULED_INTERVIEW_CONFIRMATION = 'SCHEDULED_INTERVIEW_CONFIRMATION',
    SCHEDULED_INTERVIEW_DECLINE_ACK = 'SCHEDULED_INTERVIEW_DECLINE_ACK',
    SCHEDULED_INTERVIEW_RESCHEDULE = 'SCHEDULED_INTERVIEW_RESCHEDULE',
    SCHEDULED_INTERVIEW_CANCELLATION = 'SCHEDULED_INTERVIEW_CANCELLATION',
    SCHEDULED_INTERVIEW_REJECTION = 'SCHEDULED_INTERVIEW_REJECTION',
    SCHEDULED_INTERVIEW_REMINDER_PENDING = 'SCHEDULED_INTERVIEW_REMINDER_PENDING',
    SCHEDULED_INTERVIEW_REMINDER_48H = 'SCHEDULED_INTERVIEW_REMINDER_48H',
    SCHEDULED_INTERVIEW_REMINDER_24H = 'SCHEDULED_INTERVIEW_REMINDER_24H',
    SCHEDULED_INTERVIEW_REMINDER_2H = 'SCHEDULED_INTERVIEW_REMINDER_2H',
    SCHEDULED_INTERVIEW_LOCATION_CHANGE = 'SCHEDULED_INTERVIEW_LOCATION_CHANGE',
}


export interface InterviewSchedulingFilters {
    tab?: InterviewScheduleTabEnum;
    limit?: number;
    skip?: number
    search?: string;
    dateFrom?: number;
    dateTo?: number;
    statuses?: InterviewInvitationStatusEnum[];
    results?: InterviewInvitationResultEnum[];
    recruiterIds?: string[];
    districts?: string[];
    cities?: string[];
    page?: number;
}

export interface InterviewSlotDto {
    startTime: number; // Unix timestamp in milliseconds
    endTime: number; // Unix timestamp in milliseconds
}

export interface ScheduledInterviewDto {
    externalId: string;
    status: InterviewInvitationStatusEnum;
    result?: InterviewInvitationResultEnum;
    locationType: InterviewLocationTypeEnum;
    locationName?: string;
    sentAt: number;
    expiresAt?: number;
    slot?: InterviewSlotDto;
}

export interface InterviewSlotResponseDto {
    externalId: string;
    startTime: number; // Unix timestamp in milliseconds
    endTime: number; // Unix timestamp in milliseconds
    capacity: number;
    confirmedCount: number;
    isActive: boolean;
    isFull: boolean;
    updatedAt?: number;
}

export interface CreatedByDto {
    id: string;
    name: string;
}

export interface InterviewAppointmentDetailsData {
    externalId: string;
    locationUrl: string;
    locationType: InterviewLocationTypeEnum;
    locationName?: string;
    locationInstructions?: string;
    isActive: boolean;
    slots: InterviewSlotResponseDto[];
    createdAt: number; // Unix timestamp
    updatedAt?: number; // Unix timestamp
    createdBy: CreatedByDto;
}

export interface SendInvitationsResponseData {
    invitationsSent: number;
    applicantsSkipped: number;
    skippedApplicationIds?: string[];
    appointmentDetails: InterviewAppointmentDetailsData;
}